import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import type { Pagination, ProjectIdParam, Uuid } from "@/types"

export enum ProjectActivityAction {
  ApiKeyCreated = "api_key_created",
  ApiKeyRemoved = "api_key_removed",
  DeployCreated = "deploy_created",
  DeployRemoved = "deploy_removed",
  DeployUpdated = "deploy_updated",
  CollaboratorAdded = "collaborator_added",
  CollaboratorRemoved = "collaborator_removed",
  PlanChanged = "plan_changed",
  SpendCapChanged = "spend_cap_changed",
  SpendCapHit = "spend_cap_hit",
  EntitlementChanged = "entitlement_changed",
  OwnerTransferred = "owner_transferred",
}

export const ACTION_LABELS: Record<ProjectActivityAction, string> = {
  [ProjectActivityAction.ApiKeyCreated]: "API Key Created",
  [ProjectActivityAction.ApiKeyRemoved]: "API Key Removed",
  [ProjectActivityAction.DeployCreated]: "Deploy Created",
  [ProjectActivityAction.DeployRemoved]: "Deploy Removed",
  [ProjectActivityAction.DeployUpdated]: "Deploy Updated",
  [ProjectActivityAction.CollaboratorAdded]: "Collaborator Added",
  [ProjectActivityAction.CollaboratorRemoved]: "Collaborator Removed",
  [ProjectActivityAction.PlanChanged]: "Plan Changed",
  [ProjectActivityAction.SpendCapChanged]: "Spend Cap Changed",
  [ProjectActivityAction.SpendCapHit]: "Spend Cap Hit",
  [ProjectActivityAction.EntitlementChanged]: "Entitlement Changed",
  [ProjectActivityAction.OwnerTransferred]: "Owner Transferred",
}

type DeployDetails = { deploy_id: Uuid; type: string; region: string }

export type ProjectActivityEntry =
  | ActivityEntry<ProjectActivityAction.ApiKeyCreated, { name?: string }>
  | ActivityEntry<ProjectActivityAction.ApiKeyRemoved, { name?: string }>
  | ActivityEntry<ProjectActivityAction.DeployCreated, DeployDetails>
  | ActivityEntry<ProjectActivityAction.DeployRemoved, DeployDetails>
  | ActivityEntry<ProjectActivityAction.DeployUpdated, DeployDetails>
  | ActivityEntry<ProjectActivityAction.CollaboratorAdded, { username: string }>
  | ActivityEntry<
      ProjectActivityAction.CollaboratorRemoved,
      { member_count: number }
    >
  | ActivityEntry<ProjectActivityAction.PlanChanged, { plan: string }>
  | ActivityEntry<
      ProjectActivityAction.SpendCapChanged,
      { spend_cap_cents?: number }
    >
  | ActivityEntry<ProjectActivityAction.SpendCapHit, { cap_amount: string }>
  | ActivityEntry<
      ProjectActivityAction.EntitlementChanged,
      { entitlements: string[] }
    >
  | ActivityEntry<
      ProjectActivityAction.OwnerTransferred,
      { previous_owner: string; new_owner: string }
    >

type ActivityEntry<A extends ProjectActivityAction, D> = {
  id: Uuid
  project_id: Uuid
  author_id: Uuid | null
  author_username: string | null
  action: A
  details: D
  created_at: string
}

export const projectActivityApi = createApi({
  reducerPath: "projectActivityApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}projects/`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    projectActivityGet: build.query<
      ProjectActivityEntry[],
      ProjectIdParam & Pagination
    >({
      query: ({ project_id, offset, limit }) => ({
        url: `${project_id}/activity?offset=${offset}&limit=${limit}`,
      }),
    }),
  }),
})

export const { useProjectActivityGetQuery } = projectActivityApi
